import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Sparkles, RefreshCw, CheckCircle2, AlertCircle, Clock, ExternalLink, KeyRound, Wand2, FileText, Eye, EyeOff } from "lucide-react";
import { adminBabyloveStatus, adminBabyloveSync, adminBlogGenStatus, adminBlogGenGenerate, adminBlogSetPublished } from "../../lib/api";

/**
 * Admin → BabyLoveGrowth (iter56).
 *
 * BabyLoveGrowth generates SEO articles. Their REST API is polled every
 * 4h by our scheduler and articles are upserted into db.blog_posts under
 * source="babylovegrowth". This tab shows the sync health and lets the
 * operator trigger an on-demand sync.
 */
export default function BabyloveTab({ pw }) {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [dryRun, setDryRun] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setStatus(await adminBabyloveStatus(pw));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load BabyLoveGrowth status");
    }
  }, [pw]);

  useEffect(() => { refresh(); }, [refresh]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await adminBabyloveSync(pw, dryRun);
      if (r.ok) {
        toast.success(
          dryRun
            ? `Dry run — would create ${r.created}, skipped ${r.skipped}`
            : `Synced ${r.fetched} articles — ${r.created} new, ${r.updated} updated${r.skipped ? `, ${r.skipped} skipped` : ""}`
        );
      } else {
        toast.error(`Sync failed: ${(r.errors || [])[0] || "unknown error"}`);
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (!status) return <div className="font-mono text-xs text-zinc-500">LOADING…</div>;

  const last = status.last_stats || {};
  const configured = !!status.key_configured;

  return (
    <div className="space-y-6" data-testid="babylove-tab">
      <div>
        <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
          <Sparkles size={22} className="text-[var(--tc-red)]" /> BabyLoveGrowth · SEO Auto-Publish
        </h2>
        <p className="font-body text-sm text-zinc-400 mt-1">
          Auto-imports SEO articles from{" "}
          <a href="https://www.babylovegrowth.ai" target="_blank" rel="noreferrer" className="text-[var(--tc-red)] hover:underline inline-flex items-center gap-1">
            babylovegrowth.ai <ExternalLink size={11} />
          </a>{" "}
          into your blog. Runs every 4 hours automatically; hit "Sync now" for an immediate pull.
        </p>
      </div>

      {/* Status grid */}
      <div className="grid md:grid-cols-3 gap-3">
        <div className={`border p-4 ${configured ? "border-emerald-900 bg-emerald-950/20" : "border-red-900 bg-red-950/20"}`}>
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={13} className={configured ? "text-emerald-400" : "text-red-400"} />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">API KEY</span>
          </div>
          <div className={`font-display text-xl ${configured ? "text-emerald-300" : "text-red-300"}`} data-testid="babylove-key-status">
            {configured ? "CONFIGURED" : "MISSING"}
          </div>
          {!configured && (
            <div className="font-mono text-[10px] text-zinc-500 mt-1">
              Set BABYLOVE_API_KEY in backend/.env
            </div>
          )}
        </div>
        <div className="border border-[#222] bg-black p-4">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={13} className="text-zinc-500" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">SYNCED POSTS</span>
          </div>
          <div className="font-display text-3xl text-zinc-100" data-testid="babylove-count">
            {status.synced_articles_count}
          </div>
        </div>
        <div className="border border-[#222] bg-black p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={13} className="text-zinc-500" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">LAST RUN</span>
          </div>
          <div className="font-mono text-xs text-zinc-300" data-testid="babylove-last-run">
            {status.last_run ? new Date(status.last_run).toLocaleString() : "Never"}
          </div>
          {last.fetched != null && (
            <div className="font-mono text-[10px] text-zinc-500 mt-1">
              fetched {last.fetched} · +{last.created || 0} · ~{last.updated || 0} · skip {last.skipped || 0}
            </div>
          )}
        </div>
      </div>

      {/* Sync controls */}
      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3">
        <h3 className="font-display text-lg uppercase tracking-tight">Manual sync</h3>
        <p className="font-mono text-[11px] text-zinc-500">
          Pulls every article from BabyLoveGrowth, upserts new ones, refreshes existing ones (matched by article ID / slug). Existing manually-authored posts are preserved.
        </p>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            data-testid="babylove-dry-run"
            className="accent-[var(--tc-red)]"
          />
          <span className="font-mono text-[11px] tracking-widest text-zinc-400">DRY RUN (preview — no writes)</span>
        </label>
        <button
          type="button"
          onClick={runSync}
          disabled={syncing || !configured}
          data-testid="babylove-sync-btn"
          className="bg-[var(--tc-red)] text-white px-5 py-2 font-mono text-xs tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
          {syncing ? "SYNCING…" : "SYNC NOW"}
        </button>
      </div>

      {/* Errors */}
      {last.errors && last.errors.length > 0 && (
        <div className="border border-amber-900 bg-amber-950/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={14} className="text-amber-400" />
            <span className="font-mono text-[11px] tracking-widest text-amber-200">
              LAST RUN ERRORS ({last.errors.length})
            </span>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1 font-mono text-[11px] text-zinc-400">
            {last.errors.map((e, i) => (<div key={i}>· {e}</div>))}
          </div>
        </div>
      )}
      {status.last_run && !last.errors?.length && (
        <div className="border border-emerald-900 bg-emerald-950/20 p-3 flex items-center gap-2">
          <CheckCircle2 size={14} className="text-emerald-400" />
          <span className="font-mono text-[11px] tracking-widest text-emerald-200">
            LAST SYNC OK · NO ERRORS
          </span>
        </div>
      )}

      {/* Setup walkthrough */}
      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3">
        <h3 className="font-display text-lg uppercase tracking-tight">How it works</h3>
        <ol className="list-decimal pl-5 space-y-2 text-sm text-zinc-300">
          <li>Backend polls <code className="bg-black px-1 border border-[#222] text-emerald-300">api.babylovegrowth.ai</code> every 4 hours using the <code className="bg-black px-1 border border-[#222] text-emerald-300">BABYLOVE_API_KEY</code> from <code className="bg-black px-1 border border-[#222] text-emerald-300">backend/.env</code>.</li>
          <li>Each article's HTML + JSON-LD schema is stored in <code className="bg-black px-1 border border-[#222]">db.blog_posts</code> with <code className="bg-black px-1 border border-[#222]">source: "babylovegrowth"</code>.</li>
          <li>Posts appear at <code className="bg-black px-1 border border-[#222]">/blog/{"{slug}"}</code> and in the sitemap.</li>
          <li>Idempotent — re-running the sync does nothing new unless BabyLove has fresh content.</li>
          <li>To rotate the API key: update <code className="bg-black px-1 border border-[#222]">BABYLOVE_API_KEY</code> in <code className="bg-black px-1 border border-[#222]">backend/.env</code> → restart backend → click "Sync now".</li>
        </ol>
      </div>

      {/* AI Category Blog Generator (iter65) */}
      <CategoryBlogGenerator pw={pw} />
    </div>
  );
}


/**
 * AI Category Blog Generator — one-click "generate SEO post for every
 * customize category" using Claude Sonnet 4.6. Posts save as DRAFTS
 * (published=false). Operator reviews at /blog/{slug} preview then
 * publishes via the Blog tab of ProductsTab.
 *
 * The batch endpoint runs as a background task (Cloudflare kills sync
 * requests > 100s). We poll /admin/blog/category-gen/status every 3s
 * to render live progress ("Generating [category] — 3/10 done").
 */
function CategoryBlogGenerator({ pw }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [toggling, setToggling] = useState(null); // slug currently being toggled
  // Tracks the run_id we just queued. Prevents the "prior-run done doc"
  // race where the auto-clear effect fires against a stale `phase:done`
  // status before the fresh run's status arrives.
  const [pendingRunId, setPendingRunId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const s = await adminBlogGenStatus(pw);
      setStatus(s);
      return s;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load blog-gen status");
      return null;
    }
  }, [pw]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll every 3s while:
  //   * we're waiting for our queued run_id to become visible in the status doc, OR
  //   * the backend reports any run currently running (covers page-refresh mid-run).
  const runIdMatches = pendingRunId && status?.last_run?.run_id === pendingRunId;
  const running = busy || pendingRunId || status?.last_run?.phase === "running";
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => { refresh(); }, 3000);
    return () => clearInterval(id);
  }, [running, refresh]);

  // Auto-clear busy ONLY when the fetched status doc's run_id matches
  // the run we queued (or when no run is pending — orphan cleanup path).
  useEffect(() => {
    if (!busy && !pendingRunId) return;
    const phase = status?.last_run?.phase;
    const finished = phase === "done" || phase === "error";
    if (!finished) return;
    // If we're waiting on a specific run_id, only clear when it matches.
    if (pendingRunId && status?.last_run?.run_id !== pendingRunId) return;

    setBusy(false);
    setPendingRunId(null);
    const last = status?.last_run || {};
    const err = (last.errors || []).length;
    if (phase === "error") {
      toast.error(`Generation failed: ${last.fatal_error || "unknown"}`);
    } else if (err) {
      toast.warning(`Done with ${err} error(s) — created ${last.created || 0}, updated ${last.updated || 0}, skipped ${last.skipped || 0}`);
    } else {
      toast.success(`Generated ${last.total || 0} posts in ${last.duration_s || "?"}s (${last.created || 0} new, ${last.updated || 0} updated, ${last.skipped || 0} skipped)`);
    }
  }, [busy, pendingRunId, status?.last_run]);

  const togglePublish = async (slug, currentlyPublished) => {
    setToggling(slug);
    try {
      const r = await adminBlogSetPublished(pw, slug, !currentlyPublished);
      toast.success(r.published ? `Published /blog/${slug}` : `Unpublished /blog/${slug}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setToggling(null);
    }
  };

  const runBatch = async () => {
    setBusy(true);
    try {
      const r = await adminBlogGenGenerate(pw, { onlyMissing });
      if (r.already_running) {
        toast.info("A run is already in progress — showing live progress below.");
        // Adopt the currently-running run_id so we auto-clear on completion.
        if (r.run_id) setPendingRunId(r.run_id);
      } else if (r.queued) {
        toast.info(`Queued run ${r.run_id}. Live progress updates every 3s below.`, { duration: 4000 });
        if (r.run_id) setPendingRunId(r.run_id);
      }
      await refresh();
    } catch (e) {
      setBusy(false);
      setPendingRunId(null);
      toast.error(e?.response?.data?.detail || e?.message || "Failed to queue generation");
    }
  };

  if (!status) return null;
  const last = status.last_run || {};
  const configured = !!status.key_configured;
  // Show the progress bar whenever the backend is still working on the
  // exact run we queued, OR any run is currently in-flight (page-refresh
  // mid-run). Avoids the stale-prior-run false positive.
  const isRunning = (pendingRunId && runIdMatches && last.phase === "running")
                     || (!pendingRunId && last.phase === "running");
  const progressPct = last.total_planned && last.current_index != null
    ? Math.min(100, Math.round((last.current_index / last.total_planned) * 100))
    : 0;

  return (
    <div className="space-y-4 pt-4 mt-4 border-t border-[#222]" data-testid="ai-category-blog">
      <div>
        <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
          <Wand2 size={22} className="text-[var(--tc-red)]" /> AI Category Blog · One-Click SEO
        </h2>
        <p className="font-body text-sm text-zinc-400 mt-1">
          Generates an ~800-word SEO article for every non-empty customize category using{" "}
          <strong className="text-zinc-200">{status.model}</strong>. Posts save as DRAFTS — review, then publish. Each post interlinks
          to <code className="bg-black px-1 border border-[#222] text-emerald-300">/customize/category/{"{key}"}</code>{" "}
          and embeds the top 3 products from that category as live cards.
        </p>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        <div className={`border p-4 ${configured ? "border-emerald-900 bg-emerald-950/20" : "border-red-900 bg-red-950/20"}`}>
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={13} className={configured ? "text-emerald-400" : "text-red-400"} />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">LLM KEY</span>
          </div>
          <div className={`font-display text-xl ${configured ? "text-emerald-300" : "text-red-300"}`} data-testid="blog-gen-key-status">
            {configured ? "READY" : "MISSING"}
          </div>
        </div>
        <div className="border border-[#222] bg-black p-4">
          <div className="flex items-center gap-2 mb-1">
            <FileText size={13} className="text-zinc-500" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">AI POSTS</span>
          </div>
          <div className="font-display text-3xl text-zinc-100" data-testid="blog-gen-post-count">
            {status.post_count}
          </div>
        </div>
        <div className="border border-[#222] bg-black p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle size={13} className="text-amber-400" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">DRAFTS</span>
          </div>
          <div className="font-display text-3xl text-amber-300" data-testid="blog-gen-draft-count">
            {status.draft_count}
          </div>
        </div>
        <div className="border border-[#222] bg-black p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={13} className="text-zinc-500" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">LAST RUN</span>
          </div>
          <div className="font-mono text-xs text-zinc-300" data-testid="blog-gen-last-run">
            {last.finished_at ? new Date(last.finished_at).toLocaleString() : "Never"}
          </div>
          {last.total != null && (
            <div className="font-mono text-[10px] text-zinc-500 mt-1">
              {last.total} total · +{last.created || 0} · ~{last.updated || 0}
            </div>
          )}
        </div>
      </div>

      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-3">
        <h3 className="font-display text-lg uppercase tracking-tight">Batch generate</h3>
        <p className="font-mono text-[11px] text-zinc-500">
          Loops every category in <code className="bg-black px-1 border border-[#222]">db.products.distinct('category')</code>{" "}
          and asks the LLM to draft an SEO article. Existing posts (matched by slug{" "}
          <code className="bg-black px-1 border border-[#222]">custom-{"{key}"}-toronto</code>) are refreshed in place.
          Published state is preserved — regenerating a published post keeps it published, only refreshes the body.
          Batch runs in the <strong className="text-zinc-200">background</strong> (Cloudflare kills sync requests over 100s) —
          live progress polls every 3 seconds.
        </p>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={onlyMissing}
            onChange={(e) => setOnlyMissing(e.target.checked)}
            data-testid="blog-gen-only-missing"
            className="accent-[var(--tc-red)]"
            disabled={isRunning}
          />
          <span className="font-mono text-[11px] tracking-widest text-zinc-400">ONLY MISSING (skip categories that already have a post)</span>
        </label>
        <button
          type="button"
          onClick={runBatch}
          disabled={busy || isRunning || !configured}
          data-testid="blog-gen-run-btn"
          className="bg-[var(--tc-red)] text-white px-5 py-2 font-mono text-xs tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw size={14} className={(busy || isRunning) ? "animate-spin" : ""} />
          {isRunning ? "GENERATING…" : (busy ? "QUEUING…" : "GENERATE ALL CATEGORY POSTS")}
        </button>

        {/* Live progress bar — only while phase="running" */}
        {isRunning && (
          <div className="mt-4 border border-emerald-900 bg-emerald-950/20 p-4" data-testid="blog-gen-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[11px] tracking-widest text-emerald-200">
                LIVE · {last.current_index || 0} / {last.total_planned || "?"} CATEGORIES
              </span>
              <span className="font-mono text-[11px] text-emerald-300" data-testid="blog-gen-progress-pct">
                {progressPct}%
              </span>
            </div>
            <div className="h-2 bg-black border border-[#222] overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="mt-2 font-mono text-[11px] text-zinc-400">
              {last.current_category ? (
                <>Now generating <strong className="text-emerald-300">{last.current_category}</strong>…</>
              ) : (
                "Preparing…"
              )}
              {" · "}
              <span>+{last.created || 0} new</span>{" · "}
              <span>~{last.updated || 0} updated</span>{" · "}
              <span>skip {last.skipped || 0}</span>
              {last.errors && last.errors.length > 0 && (
                <>{" · "}<span className="text-red-400">{last.errors.length} errors</span></>
              )}
            </div>
            {last.run_id && (
              <div className="mt-1 font-mono text-[9px] text-zinc-600" data-testid="blog-gen-run-id">
                run_id: {last.run_id}
              </div>
            )}
          </div>
        )}
      </div>

      {status.posts && status.posts.length > 0 && (
        <div className="border border-[#222] bg-black p-4" data-testid="blog-gen-posts-list">
          <div className="font-mono text-[11px] tracking-widest text-zinc-500 mb-3">
            AI POSTS · {status.posts.length} TOTAL · {status.draft_count} DRAFTS · {status.published_count} PUBLISHED
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-[#222]">
            {status.posts.map((p) => {
              const isBusy = toggling === p.slug;
              return (
                <div key={p.slug} className="flex items-center gap-3 py-2.5 px-1" data-testid={`blog-post-row-${p.slug}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[11px] text-zinc-200 truncate">{p.title}</div>
                    <div className="font-mono text-[10px] text-zinc-500 flex items-center gap-2">
                      <span>/{p.category_key || "?"}</span>
                      <span>·</span>
                      <span>updated {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}</span>
                    </div>
                  </div>
                  <span
                    className={`font-mono text-[9px] tracking-widest px-2 py-1 border ${
                      p.published
                        ? "border-emerald-900 bg-emerald-950/30 text-emerald-300"
                        : "border-amber-900 bg-amber-950/30 text-amber-300"
                    }`}
                    data-testid={`blog-post-status-${p.slug}`}
                  >
                    {p.published ? "LIVE" : "DRAFT"}
                  </span>
                  <a
                    href={`/blog/${p.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-[10px] text-zinc-400 hover:text-white inline-flex items-center gap-1"
                    data-testid={`blog-post-preview-${p.slug}`}
                  >
                    preview <ExternalLink size={9} />
                  </a>
                  <button
                    type="button"
                    onClick={() => togglePublish(p.slug, p.published)}
                    disabled={isBusy}
                    data-testid={`blog-post-toggle-${p.slug}`}
                    className={`font-mono text-[10px] tracking-widest px-2.5 py-1 border transition-colors disabled:opacity-50 inline-flex items-center gap-1.5 ${
                      p.published
                        ? "border-[#333] bg-black text-zinc-300 hover:border-amber-800 hover:text-amber-300"
                        : "border-emerald-900 bg-emerald-950/20 text-emerald-300 hover:bg-emerald-950/50"
                    }`}
                  >
                    {p.published ? <><EyeOff size={10} /> UNPUBLISH</> : <><Eye size={10} /> PUBLISH</>}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {last.errors && last.errors.length > 0 && (
        <div className="border border-red-900 bg-red-950/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={14} className="text-red-400" />
            <span className="font-mono text-[11px] tracking-widest text-red-200">
              LAST RUN ERRORS ({last.errors.length})
            </span>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1 font-mono text-[11px] text-red-300">
            {last.errors.map((e, i) => (<div key={i}>· {e.category}: {e.error}</div>))}
          </div>
        </div>
      )}

    </div>
  );
}
